<template>
  <div class="bg-gray-100 h-screen w-full relative overflow-hidden flex flex-col">
    <!-- 顶部导航栏 -->
    <div
      class="bg-blue-500 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 text-2xl shrink-0"
    >
      <div class="flex items-center">
        <router-link to="/tableManage" class="text-white mr-3">
          <i class="fas fa-chevron-left"></i>
        </router-link>
        <h1 id="table-title" class="text-xl font-semibold text-2xl">
          {{ shopName || shopDetail?.shopName || '' }} - {{ tableName }} - 点餐
        </h1>
      </div>
      <div class="flex items-center">
        <button class="mr-4" @click="goToHistory">
          <i class="fas fa-history text-white"></i>
        </button>
        <button @click="showMenu">
          <i class="fas fa-ellipsis-v text-white"></i>
        </button>
      </div>
    </div>

    <!-- 用餐信息 -->
    <div class="bg-white p-4 mb-2 flex justify-between items-center shrink-0">
      <div class="flex items-center">
        <span class="text-gray-500 mr-2 text-xl">用餐人数:</span>
        <div class="flex border rounded overflow-hidden">
          <button
            class="px-3 py-1 bg-gray-100 text-gray-700"
            :disabled="hasPendingPayment"
            @click="decreasePeople"
            >-</button
          >
          <span
            id="people-count"
            class="px-3 py-1 border-l border-r flex items-center justify-center min-w-[40px] text-2xl"
            >{{ peopleCount }}</span
          >
          <button
            class="px-3 py-1 bg-gray-100 text-gray-700"
            :disabled="hasPendingPayment"
            @click="increasePeople"
            >+</button
          >
        </div>
      </div>
      <div class="flex items-center text-2xl">
        <span class="text-gray-500 mr-2">就餐时间:</span>
        <span id="current-time">{{ currentTime }}</span>
      </div>
      <button
        v-if="isSpecialOrder"
        class="flex items-center text-xl px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50"
        :class="selfPickDateTime ? 'text-gray-800' : 'text-blue-500'"
        :disabled="hasPendingPayment"
        @click="showTimePicker"
      >
        <span class="text-red-500 mr-1">*</span>
        <span class="text-gray-500 mr-2">{{ timeLabel }}:</span>
        <span>{{ selfPickDateTime || '请选择' }}</span>
        <i class="fas fa-chevron-right ml-2 text-gray-400"></i>
      </button>
      <!-- <button class="bg-blue-500 text-white px-4 py-2 rounded-lg text-2xl" @click="showQRCode">
        <i class="fas fa-qrcode mr-1"></i> 扫码点餐
          </button> -->
    </div>

    <!-- 菜单和订单区域 -->
    <div class="flex flex-row flex-1 min-h-0 overflow-hidden">
      <!-- 左侧分类导航 -->
      <div class="w-1/4 bg-white overflow-y-auto border-r text-2xl min-h-0">
        <div class="sticky top-0 bg-gray-200 p-3">
          <div class="relative">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索菜品"
              class="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <i class="fas fa-search absolute right-3 top-3 text-gray-400"></i>
          </div>
        </div>
        <div class="p-5">
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="category-item"
            :class="{ active: selectedCategory === cat.name }"
            @click="selectCategory(cat.name || '')"
          >
            <!-- <i class="fas fa-utensils mr-2"></i> -->
            {{ cat.name }}
          </div>
        </div>
      </div>

      <!-- 中间菜品列表 -->
      <div class="w-2/4 bg-white overflow-y-auto border-r text-2xl min-h-0">
        <div class="p-3 bg-gray-100 sticky top-0 z-10">
          <h2 class="font-bold">{{ selectedCategory }}</h2>
        </div>
        <div class="menu-items">
          <div
            v-for="item in filteredMenuItems"
            :key="item.id"
            class="menu-item"
            :class="{ 'menu-item-disabled': (item.stockQuantity ?? 0) <= 0 }"
            :aria-disabled="(item.stockQuantity ?? 0) <= 0"
            @click="showSpecDialog(item, $event)"
          >
            <div class="menu-item-image">
              <img :src="item.image" :alt="item.name" class="object-cover w-full h-full" />
            </div>
            <div class="menu-item-content">
              <h3 class="menu-item-title">{{ item.name }}</h3>
              <p class="menu-item-desc">{{ item.description }}</p>
              <div class="flex justify-between items-center">
                <div class="flex items-center">
                  <span class="menu-item-price">¥{{ item.price }}</span>
                  <span
                    class="menu-item-stock"
                    :class="{ 'out-of-stock': (item.stockQuantity ?? 0) <= 0 }"
                  >
                    库存：{{ item.stockQuantity ?? 0 }}
                  </span>
                </div>
                <div class="flex items-center hidden">
                  <button
                    v-if="getOrderItemCount(item.name || '') > 0"
                    class="menu-item-btn decrease"
                    @click.stop="decreaseItem(item.name || '')"
                    >-</button
                  >
                  <span v-if="getOrderItemCount(item.name || '') > 0" class="menu-item-count">{{
                    getOrderItemCount(item.name || '')
                  }}</span>
                  <button class="menu-item-btn increase" @click.stop="showSpecDialog(item, $event)"
                    >+</button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧订单区域 -->
      <div ref="orderPanel" class="w-1/4 bg-white flex flex-col overflow-hidden min-h-0">
        <div
          class="px-3 py-2 bg-gray-100 sticky top-0 z-10 flex justify-between items-center text-xl shrink-0"
        >
          <h2 class="font-bold">已点菜品</h2>
          <button
            class="text-red-500 text-xl"
            :class="{ 'opacity-50': hasPendingPayment }"
            :disabled="hasPendingPayment"
            @click="clearOrder"
          >
            <i class="fas fa-trash-alt mr-1"></i> 清空
          </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto px-3 py-1">
          <div v-if="orderItems.length === 0" class="text-center text-gray-500 py-10">
            <i class="fas fa-utensils text-5xl mb-2"></i>
            <p>暂无点餐，请从左侧选择菜品</p>
          </div>
          <div v-else>
            <div v-for="item in orderItems" :key="item.name" class="order-item">
              <div class="order-item-info">
                <div class="order-item-name">{{ item.name }}</div>
                <div
                  v-if="
                    (item.specName && item.specName !== '标准') ||
                    (item.methodName && item.methodName.trim() !== '')
                  "
                  class="order-item-spec text-gray-500"
                >
                  <template v-if="item.specName && item.specName !== '标准'">{{
                    item.specName
                  }}</template>
                  <template v-if="item.methodName && item.methodName.trim() !== ''">
                    {{ item.specName && item.specName !== '标准' ? ' - ' : ''
                    }}{{ item.methodName }}
                  </template>
                </div>
                <div class="order-item-price">¥{{ item.price }} × {{ item.quantity }}</div>
              </div>
              <div class="order-item-controls">
                <button
                  class="menu-item-btn decrease mr-2"
                  :disabled="hasPendingPayment"
                  @click="decreaseItem(item.name)"
                  >-</button
                >
                <span class="menu-item-count mr-2">{{ item.quantity }}</span>
                <button
                  class="menu-item-btn increase"
                  :disabled="hasPendingPayment"
                  @click="showSpecDialog(item, $event)"
                  >+</button
                >
              </div>
            </div>
          </div>
        </div>
        <div class="px-3 py-2 border-t text-xl shrink-0 bg-white">
          <div class="mb-2 flex items-center">
            <label for="order-description" class="text-lg text-gray-700 shrink-0 mr-2"
              >订单备注</label
            >
            <input
              id="order-description"
              v-model="orderDescription"
              type="text"
              maxlength="200"
              placeholder="选填"
              :disabled="hasPendingPayment"
              class="min-w-0 flex-1 px-3 py-1 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex justify-between mb-2">
            <span
              >小计
              <span class="text-base text-lg text-gray-500">（共{{ totalCount }}份）</span></span
            >
            <span>¥{{ subtotal.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between items-center mb-2">
            <div class="flex items-center">
              <span>折扣金额</span>
              <button
                class="ml-2 text-blue-500 hover:text-blue-700 text-sm"
                title="点击设置折扣"
                :disabled="hasPendingPayment"
                @click="adjustDiscount"
              >
                <i class="fas fa-edit"></i>
              </button>
            </div>
            <span class="text-red-500">-¥{{ discountAmount.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between font-bold">
            <span>总计</span>
            <span>¥{{ total.toFixed(2) }}</span>
          </div>
          <button
            class="w-full bg-blue-500 text-white py-2 rounded-lg mt-2 font-bold"
            :disabled="orderItems.length === 0 || isSubmittingOrder"
            :class="{ 'opacity-50': orderItems.length === 0 || isSubmittingOrder }"
            @click="submitOrder"
          >
            {{ isSubmittingOrder ? '提交中...' : hasPendingPayment ? '继续支付' : '提交订单' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 折扣设置弹窗 -->
    <div
      v-if="discountModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-xl p-6 w-80">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">设置折扣</h3>
          <button @click="hideDiscountModal">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>
        <div class="mb-4">
          <div class="grid grid-cols-2 gap-2 mb-4">
            <button
              class="py-2 rounded-lg border"
              :class="
                tempDiscountMode === 'amount'
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-300 text-gray-600'
              "
              @click="tempDiscountMode = 'amount'"
            >
              按金额
            </button>
            <button
              class="py-2 rounded-lg border"
              :class="
                tempDiscountMode === 'rate'
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-300 text-gray-600'
              "
              @click="tempDiscountMode = 'rate'"
            >
              按比例
            </button>
          </div>
          <div v-if="tempDiscountMode === 'amount'">
            <label class="block text-sm font-medium text-gray-700 mb-2">折扣金额</label>
            <input
              v-model.number="tempManualDiscountAmount"
              type="number"
              min="0"
              step="0.01"
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="请输入折扣金额"
            />
          </div>
          <div v-else>
            <label class="block text-sm font-medium text-gray-700 mb-2">折扣比例</label>
            <select
              v-model.number="tempDiscountRate"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              <option :value="1">无折扣（100%）</option>
              <option :value="0.95">95%</option>
              <option :value="0.9">90%</option>
              <option :value="0.85">85%</option>
              <option :value="0.8">80%</option>
              <option :value="0.7">70%</option>
              <option :value="0.5">50%</option>
            </select>
          </div>
          <div class="text-sm text-gray-500 mt-3">当前小计：¥{{ subtotal.toFixed(2) }}</div>
        </div>
        <div class="flex space-x-3">
          <button
            class="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            @click="hideDiscountModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            @click="confirmDiscount"
          >
            确认
          </button>
        </div>
      </div>
    </div>

    <!-- 二维码弹窗 -->
    <div
      v-if="qrModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-xl p-6 w-80">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">扫码点餐</h3>
          <button @click="hideQRCode">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>
        <div class="text-center">
          <div class="w-48 h-48 mx-auto bg-gray-100 flex items-center justify-center mb-4">
            <i class="fas fa-qrcode text-8xl text-gray-400"></i>
          </div>
          <p class="text-gray-600 mb-2">请顾客扫描二维码</p>
          <p class="text-gray-600">自助点餐</p>
        </div>
      </div>
    </div>

    <!-- 支付确认模态框 -->
    <div
      v-if="paymentConfirmModalVisible"
      class="payment-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="payment-modal-card bg-white rounded-xl">
        <div class="payment-modal-header flex justify-between items-center">
          <h3 class="text-xl font-bold">选择支付方式</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelPaymentConfirmation">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="payment-modal-body">
          <!-- 订单信息 -->
          <div class="payment-order-summary p-3 bg-gray-50 rounded-lg">
            <div class="flex justify-between items-center mb-2">
              <span class="text-gray-600">订单号:</span>
              <span class="font-medium">{{ currentOrderNo || '提交后生成' }}</span>
            </div>
            <div class="flex justify-between items-center mb-2">
              <span class="text-gray-600">桌号:</span>
              <span class="font-medium">{{ tableName }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-600">应付金额:</span>
              <span class="text-xl font-bold text-red-500">¥{{ total.toFixed(2) }}</span>
            </div>
          </div>

          <div class="payment-form space-y-4">
            <!-- 支付方式选择 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"> 支付方式 </label>
              <select
                v-model="selectedPaymentMethod"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                @change="handlePaymentMethodChange"
              >
                <option v-for="method in paymentMethods" :key="method.value" :value="method.value">
                  {{ method.name }}
                </option>
              </select>
            </div>

            <div v-if="selectedPaymentMethod === 100" class="p-3 bg-yellow-50 rounded-lg">
              <p v-if="unsupportedFoodTicketNames.length > 0" class="mb-3 text-sm text-red-500">
                以下菜品不支持餐券使用，请删除后再使用餐券支付：{{
                  unsupportedFoodTicketNames.join('、')
                }}
              </p>
              <label class="block text-sm font-medium text-gray-700 mb-2">员工工号</label>
              <div class="employee-validation-row">
                <input
                  v-model.trim="foodTicketEmployeeNo"
                  type="text"
                  inputmode="numeric"
                  placeholder="请输入员工工号"
                  :disabled="Boolean(foodTicketCustomer) || hasPendingPayment"
                  class="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @keyup.enter="validateFoodTicketEmployee"
                />
                <button
                  v-if="!foodTicketCustomer"
                  class="employee-validation-button px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  :disabled="isValidatingEmployee || hasPendingPayment"
                  @click="validateFoodTicketEmployee"
                >
                  {{ isValidatingEmployee ? '查询中' : '验证' }}
                </button>
                <button
                  v-else-if="!hasPendingPayment"
                  class="employee-validation-button px-4 py-2 border border-blue-500 text-blue-600 rounded-lg"
                  @click="resetFoodTicketEmployee"
                >
                  重选
                </button>
              </div>
              <div v-if="foodTicketCustomer" class="mt-3 text-sm text-gray-700 space-y-1">
                <div>员工：{{ foodTicketCustomer.customerName }}</div>
                <div>
                  餐券余额：
                  <span
                    class="font-semibold"
                    :class="hasEnoughFoodTicketBalance ? 'text-green-600' : 'text-red-500'"
                  >
                    ¥{{ Number(foodTicketCustomer.foodTicketAccount || 0).toFixed(2) }}
                  </span>
                </div>
                <div v-if="!hasEnoughFoodTicketBalance" class="text-red-500">
                  餐券余额不足，请选择其他支付方式
                </div>
              </div>
              <div v-else-if="hasPendingPayment" class="mt-2 text-sm text-red-500">
                该订单创建时未绑定员工，不能改用餐券支付
              </div>
            </div>

            <!-- 备注输入 -->
            <div v-if="selectedPaymentMethod !== 3">
              <label class="block text-sm font-medium text-gray-700 mb-2"> 备注信息 </label>
              <textarea
                v-model="paymentRemark"
                placeholder="请输入备注信息（可选）"
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base resize-none"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="payment-modal-footer flex space-x-3 border-t">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="cancelPaymentConfirmation"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            :class="{ 'opacity-50': isSubmittingOrder || isValidatingEmployee }"
            :disabled="isSubmittingOrder || isValidatingEmployee"
            @click="handlePaymentConfirm"
          >
            {{ isSubmittingOrder ? '提交中...' : '确认支付' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 扫码支付全屏界面 -->
    <div v-if="scanPaymentModalVisible" class="fixed inset-0 bg-black z-50 flex flex-col">
      <!-- 顶部导航栏 -->
      <div class="bg-black bg-opacity-80 text-white px-4 py-3 flex items-center justify-between">
        <button class="text-white" @click="cancelScanPayment">
          <i class="fas fa-times text-2xl"></i>
        </button>
        <h3 class="text-xl font-bold">B扫C收款</h3>
        <div class="w-8"></div>
      </div>

      <!-- 摄像头扫描区域 -->
      <div class="flex-1 relative overflow-hidden w-full h-full">
        <div id="qr-reader" class="qr-reader-container w-full h-full"></div>

        <!-- 动态扫描线 -->
        <div v-if="isScanning" class="absolute inset-0 pointer-events-none z-20">
          <div class="scan-line"></div>
        </div>
      </div>

      <!-- 底部状态提示 -->
      <div v-if="scanStatus" class="bg-black bg-opacity-80 px-4 py-3">
        <div class="p-3 rounded-lg text-center" :class="scanStatusClass">
          <p class="text-sm font-medium">{{ scanStatus }}</p>
        </div>
      </div>
    </div>

    <!-- 扫码支付失败确认模态框 -->
    <div
      v-if="scanPaymentFailModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">支付确认</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelScanPaymentConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mb-6">
          <p class="text-gray-700 text-lg">支付结果未确认，请选择下一步操作。</p>
          <p v-if="scanPaymentFailErrorMsg" class="text-red-500 text-sm mt-2">
            错误信息：{{ scanPaymentFailErrorMsg }}
          </p>
        </div>

        <!-- 操作按钮 -->
        <div class="flex flex-col space-y-3">
          <button
            :disabled="isRefreshingPayment"
            :class="[
              'w-full py-3 rounded-lg transition-colors font-medium',
              isRefreshingPayment
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600',
            ]"
            @click="confirmScanPaymentPaid"
          >
            <span v-if="isRefreshingPayment">
              <i class="fas fa-spinner fa-spin mr-2"></i>查询支付结果中
            </span>
            <span v-else>重新查询支付结果</span>
          </button>
          <button
            :disabled="isRefreshingPayment"
            class="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            @click="retryScanPayment"
          >
            重新扫码支付
          </button>
          <button
            :disabled="isRefreshingPayment"
            class="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            @click="cancelScanPaymentConfirm"
          >
            取消
          </button>
        </div>
      </div>
    </div>

    <!-- 规格选择弹窗 -->
    <div
      v-if="showSpecModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-modal="spec-modal"
    >
      <div class="bg-white rounded-xl p-6 w-80 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">选择规格</h3>
          <button @click="hideSpecModal">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>

        <!-- 菜品信息 -->
        <div class="mb-4 p-3 bg-gray-50 rounded-lg">
          <div class="flex items-center">
            <img
              :src="currentFood.image"
              :alt="currentFood.name"
              class="w-16 h-16 rounded-lg mr-3 object-cover"
            />
            <div>
              <h4 class="font-medium">{{ currentFood.name }}</h4>
              <p class="text-gray-600 text-sm">{{ currentFood.description }}</p>
            </div>
          </div>
        </div>

        <!-- 规格选择 -->
        <div class="mb-4">
          <h4 class="text-lg font-medium mb-3">选择规格</h4>
          <div class="space-y-2">
            <button
              v-for="spec in currentFood.specs || []"
              :key="spec.id"
              class="w-full p-3 rounded-lg border flex items-center justify-between"
              :class="
                selectedSpec.id === spec.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              "
              @click="selectedSpec = spec"
            >
              <span>{{ spec.name }}</span>
              <span class="font-medium">¥{{ spec.price }}</span>
              <i v-if="selectedSpec.id === spec.id" class="fas fa-check text-blue-500"></i>
            </button>
          </div>
        </div>

        <!-- 做法选择 -->
        <div v-if="methods.length > 0" class="mb-4">
          <div
            v-for="(attributeGroup, attributeName) in groupedMethods"
            :key="attributeName"
            class="mb-4"
          >
            <h4 class="text-lg font-medium mb-3">选择{{ attributeName }}</h4>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="method in attributeGroup"
                :key="method.id"
                class="px-3 py-1 rounded-full border text-sm"
                :class="
                  isMethodSelected(attributeName, method.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-300 text-gray-600'
                "
                @click="toggleMethodSelection(attributeName, method.id)"
              >
                {{ method.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- 确认按钮 -->
        <div class="flex space-x-3">
          <button
            class="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            @click="hideSpecModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            @click="confirmAddToOrder"
          >
            确认添加
          </button>
        </div>
      </div>
    </div>

    <van-popup v-model:show="timePickerVisible" round position="bottom">
      <van-picker
        v-model="timePickerValues"
        :columns="timePickerColumns"
        :title="`选择${timeLabel}`"
        @cancel="timePickerVisible = false"
        @change="handleTimePickerChange"
        @confirm="handleTimePickerConfirm"
      />
    </van-popup>
  </div>

  <!-- 确认对话框 -->
  <ConfirmDialog ref="confirmDialog" :message="confirmMessage" :title="confirmTitle" />
</template>

<script setup lang="ts">
  import { CategoryModel, CategoryItemModel } from '@/model/food';
  import { fetchCategoryItemList, foodUp, fetchCategoryTagList } from '@/service/food';
  import { fetchAddOrder, insertOrderPayment, completeOrder } from '@/service/order';
  import {
    fetchShopDetail,
    getBookTimeList,
    getDeliveryTimeList,
    getSelfPickTimeList,
    getShopConfigList,
  } from '@/service/base';
  import { getCustomerList } from '@/service/customer';
  import { showDialog, showToast } from 'vant';
  import type { PickerChangeEventParams, PickerConfirmEventParams, PickerOption } from 'vant';
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import ConfirmDialog from '@/components/ConfirmDialog.vue';
  import { OrderPaymentModel } from '@/model/orderPayment';
  import dayjs from 'dayjs';
  import { dateTimeFormat } from '@/utils/utils';
  import { scanPayment, refreshScanPayment } from '@/service/payment';
  import { Html5Qrcode } from 'html5-qrcode';
  import { useCurrentEmployee } from '@/composables/useCurrentEmployee';
  import { CustomerModel } from '@/model/customer';

  const router = useRouter();
  const route = useRoute();
  const { employeeId, employeeName } = useCurrentEmployee();

  const tableNumber = ref(0);
  const tableName = ref('');
  const tableTag = ref('');
  const shopId = ref(0);
  const shopName = ref('');
  const cartType = ref(1); // 新增：1-正常点餐，2-直接支付
  const peopleCount = ref(1);
  const orderDescription = ref('');
  const currentTime = ref('');
  const searchKeyword = ref('');
  const qrModalVisible = ref(false);
  const timePickerVisible = ref(false);
  const selfPickDate = ref('');
  const selfPickTime = ref('');
  const selfPickDateTime = computed(() =>
    selfPickDate.value && selfPickTime.value ? `${selfPickDate.value} ${selfPickTime.value}` : '',
  );
  const availableDates = ref<string[]>([]);
  const availableTimeMap = ref<Record<string, string[]>>({});
  const selectedPickerDate = ref('');
  const timePickerValues = ref<Array<string | number>>([]);

  const getMenuTag = (tag: string, name: string): string => {
    const normalizedTag = tag.trim();
    if (normalizedTag) return normalizedTag;

    // 特殊桌可能只通过桌名区分；普通桌的空标签仍按堂食处理。
    if (name === '自提' || name === 'Pickup') return '自提';
    if (name === '外卖' || name === 'Delivery') return '外卖';
    if (name === '预定' || name === 'Reservation') return '预定';
    return '堂食';
  };

  const menuTag = computed(() => getMenuTag(tableTag.value, tableName.value));

  const getDineTypeByMenuTag = (tag: string): number => {
    if (tag === '自提') return 2;
    if (tag === '外卖') return 3;
    if (tag === '预定') return 5;
    return 1;
  };

  const dineType = computed(() => getDineTypeByMenuTag(menuTag.value));
  const isSpecialOrder = computed(() => [2, 3, 5].includes(dineType.value));
  const timeLabel = computed(() => {
    if (dineType.value === 2) return '自提时间';
    if (dineType.value === 3) return '外卖时间';
    if (dineType.value === 5) return '预定时间';
    return '就餐时间';
  });
  const timePickerColumns = computed<PickerOption[][]>(() => [
    availableDates.value.map((date) => ({ text: date, value: date })),
    (availableTimeMap.value[selectedPickerDate.value] || []).map((time) => ({
      text: time,
      value: time,
    })),
  ]);

  // 确认对话框相关
  const confirmDialog = ref<InstanceType<typeof ConfirmDialog>>();
  const confirmMessage = ref('');
  const confirmTitle = ref('');

  // 支付相关状态
  const paymentConfirmModalVisible = ref(false);
  const selectedPaymentMethod = ref(4); // 默认选择POS支付，如果有B扫C收款会自动更新
  const paymentRemark = ref('');
  const currentOrderId = ref(0);
  const currentOrderNo = ref('');
  const tableOrderCompleteType = ref<number | null>(null); // 保存订单返回的tableOrderCompleteType
  const isSubmittingOrder = ref(false);
  const hasPendingPayment = computed(() => cartType.value === 2 && currentOrderId.value > 0);
  const foodTicketEnabled = ref(false);
  const foodTicketEmployeeNo = ref('');
  const foodTicketCustomer = ref<CustomerModel | null>(null);
  const isValidatingEmployee = ref(false);
  const hasEnoughFoodTicketBalance = computed(
    () =>
      Boolean(foodTicketCustomer.value) &&
      Number(foodTicketCustomer.value?.foodTicketAccount || 0) >= total.value,
  );

  // 扫码支付相关状态
  const scanPaymentModalVisible = ref(false);
  const isScanning = ref(false);
  const scanPaymentRemark = ref('');
  const scanStatus = ref('');
  const scanStatusClass = ref('');
  const scanPaymentFailModalVisible = ref(false); // 扫码支付失败确认模态框
  const scanPaymentFailErrorMsg = ref(''); // 扫码支付失败错误信息
  const isRefreshingPayment = ref(false); // 是否正在刷新支付状态
  let html5QrCode: Html5Qrcode | null = null;

  // 店铺详情和营业时间相关状态
  const shopDetail = ref<any>(null);
  const isOnlinePayment = ref(false); // 是否为线上支付
  const businessTimeList = ref<Array<{ beginTime: string; endTime: string }>>([]);

  type DiscountMode = 'amount' | 'rate';
  const discountMode = ref<DiscountMode>('rate');
  const manualDiscountAmount = ref(0);
  const discountRate = ref(1);
  const discountModalVisible = ref(false);
  const tempDiscountMode = ref<DiscountMode>('rate');
  const tempManualDiscountAmount = ref(0);
  const tempDiscountRate = ref(1);

  // 规格选择相关状态
  const showSpecModalVisible = ref(false);
  const currentFood = ref<any>({});
  const selectedSpec = ref<any>({});
  const selectedMethod = ref(0);
  const selectedMethods = ref<{ [key: string]: number | number[] }>({}); // 支持多个属性选择
  const currentEvent = ref<Event | undefined>(undefined); // 保存原始点击事件
  const methods = ref<
    Array<{
      id: number;
      name: string;
      cookId?: number;
      cookName?: string;
      cookType?: number;
      attributeName?: string; // 新增属性名称字段
    }>
  >([]);

  const categories = ref<CategoryModel[]>([]);

  const selectedCategory = ref('');

  const orderItems = ref<
    {
      id: number;
      name: string;
      price: number;
      quantity: number;
      shopFoodSpecId: number;
      isFoodTicket?: 0 | 1;
      specName?: string;
      methodName?: string;
    }[]
  >([]);

  const unsupportedFoodTicketNames = computed(() => [
    ...new Set(orderItems.value.filter((item) => item.isFoodTicket !== 1).map((item) => item.name)),
  ]);

  const orderPanel = ref<HTMLElement | null>(null);

  // 支付方式列表
  const paymentMethods = computed(() => {
    const methods = [
      { value: 4, name: 'POS支付' },
      { value: 5, name: '公司挂账' },
      { value: 7, name: '现金支付' },
    ];
    // 如果支持在线支付，添加B扫C收款选项
    if (isOnlinePayment.value) {
      methods.unshift({ value: 3, name: 'B扫C收款' });
    }
    if (foodTicketEnabled.value && (!hasPendingPayment.value || foodTicketCustomer.value)) {
      methods.push({ value: 100, name: '餐券支付' });
    }
    return methods;
  });

  // 从登录态获取工号
  const getEmployeeId = () => {
    return employeeId.value;
  };

  // 从登录态获取员工姓名
  const getEmployeeName = () => {
    return employeeName.value;
  };

  // 按属性名称分组的计算属性
  const groupedMethods = computed(() => {
    const groups: { [key: string]: any[] } = {};
    methods.value.forEach((method) => {
      const attributeName = method.attributeName || method.cookName || '做法';
      if (!groups[attributeName]) {
        groups[attributeName] = [];
      }
      groups[attributeName].push(method);
    });
    return groups;
  });

  // 所有菜品的完整列表，用于左侧选择“全部”时展示
  const allMenuItems = ref<CategoryItemModel[]>([]);

  const filteredMenuItems = computed<CategoryItemModel[]>(() => {
    let items: CategoryItemModel[] = [];

    if (selectedCategory.value === '全部') {
      // 如果是"全部"分类，直接展示所有菜品（不再通过标签/分类拼接）
      items = allMenuItems.value;
    } else {
      // 否则只显示选中分类的菜品
      const selectedCat = categories.value.find((cat) => cat.name === selectedCategory.value);
      items = selectedCat?.items || [];
    }

    const filteredItems = searchKeyword.value
      ? items.filter(
          (item) =>
            item.name?.includes(searchKeyword.value) ||
            item.description?.includes(searchKeyword.value),
        )
      : items;

    return [...filteredItems].sort((firstItem, secondItem) => {
      const firstOutOfStock = (firstItem.stockQuantity ?? 0) <= 0;
      const secondOutOfStock = (secondItem.stockQuantity ?? 0) <= 0;
      return Number(firstOutOfStock) - Number(secondOutOfStock);
    });
  });

  const getOrderItemCount = (name: string) => {
    const found = orderItems.value.find((item) => item.name === name);
    return found ? found.quantity : 0;
  };

  const addToOrder = (item: any, event: any) => {
    if (hasPendingPayment.value) return;
    if ((item.stockQuantity ?? 0) <= 0) {
      showToast('该菜品库存不足，暂时无法添加');
      return;
    }
    if (!item.specs || item.specs.length === 0) {
      showToast('该菜品未配置规格，暂时无法添加');
      return;
    }
    // 1. 订单逻辑
    const found = orderItems.value.find((o) => o.id === item.id);
    if (found) {
      found.quantity += 1;
    } else {
      orderItems.value.push({ ...item, quantity: 1 });
    }
    // 2. 动画 - 等待DOM更新后执行
    nextTick(() => {
      flyToOrder(event);
    });
  };

  const flyToOrder = (event: any) => {
    console.log('开始飞入动画', event);

    const menuItem = event.target.closest('.menu-item');
    if (!menuItem) {
      console.log('未找到menu-item元素');
      return;
    }

    const img = menuItem.querySelector('img');
    if (!img) {
      console.log('未找到图片元素');
      return;
    }

    if (!orderPanel.value) {
      console.log('未找到订单面板元素');
      return;
    }

    const imgRect = img.getBoundingClientRect();
    const orderRect = orderPanel.value.getBoundingClientRect();

    console.log('图片位置:', imgRect);
    console.log('订单面板位置:', orderRect);

    const clone = img.cloneNode(true) as HTMLImageElement;
    clone.style.position = 'fixed';
    clone.style.left = imgRect.left + 'px';
    clone.style.top = imgRect.top + 'px';
    clone.style.width = imgRect.width + 'px';
    clone.style.height = imgRect.height + 'px';
    clone.style.zIndex = '9999';
    clone.style.borderRadius = '8px';
    clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
    clone.style.transition = 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    document.body.appendChild(clone);

    // 查找订单区中该商品的元素
    let orderTarget = null;
    const orderItemsElements = orderPanel.value.querySelectorAll('.order-item');
    orderItemsElements.forEach((orderItem: any) => {
      const orderName = orderItem.querySelector('.order-item-name');
      if (
        orderName &&
        orderName.textContent ===
          (event.target.closest('.menu-item')?.querySelector('.menu-item-title')?.textContent || '')
      ) {
        orderTarget = orderItem;
      }
    });

    let targetX, targetY;
    if (orderTarget) {
      // 已有该商品，飞向该商品
      const orderItemRect = (orderTarget as HTMLElement).getBoundingClientRect();
      targetX = orderItemRect.left + orderItemRect.width / 2 - imgRect.width / 2;
      targetY = orderItemRect.top + orderItemRect.height / 2 - imgRect.height / 2;
    } else {
      // 没有该商品，飞向订单区顶部
      targetX = orderRect.left + orderRect.width / 2 - imgRect.width / 2;
      targetY = orderRect.top + 30;
    }

    nextTick(() => {
      console.log('目标位置:', { targetX, targetY });

      clone.style.transform = `translate(${targetX - imgRect.left}px, ${
        targetY - imgRect.top
      }px) scale(0.3)`;
      clone.style.opacity = '0.5';
    });

    setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
      console.log('动画完成，移除克隆元素');
    }, 1200);
  };

  const decreaseItem = (name: string) => {
    if (hasPendingPayment.value) return;
    const idx = orderItems.value.findIndex((item) => item.name === name);
    if (idx !== -1) {
      if (orderItems.value[idx].quantity > 1) {
        orderItems.value[idx].quantity -= 1;
      } else {
        orderItems.value.splice(idx, 1);
      }
    }
  };

  const clearOrder = async () => {
    if (hasPendingPayment.value) return;
    if (orderItems.value.length === 0) return;
    confirmMessage.value = '确定要清空所有已点菜品吗？';
    confirmTitle.value = '清空确认';
    if (await confirmDialog.value?.show()) {
      orderItems.value = [];
    }
  };

  const subtotal = computed(() =>
    orderItems.value.reduce((sum, item) => sum + item.price * item.quantity, 0),
  );
  const discountAmount = computed(() => {
    const amount =
      discountMode.value === 'amount'
        ? manualDiscountAmount.value
        : subtotal.value * (1 - discountRate.value);
    return Math.min(subtotal.value, Math.max(0, Math.round(amount * 100) / 100));
  });
  const total = computed(() => Math.max(0, subtotal.value - discountAmount.value));
  const totalCount = computed(() => orderItems.value.reduce((sum, item) => sum + item.quantity, 0));

  const selectCategory = (name: string) => {
    selectedCategory.value = name;
  };

  const increasePeople = () => {
    if (hasPendingPayment.value) return;
    peopleCount.value += 1;
  };
  const decreasePeople = () => {
    if (hasPendingPayment.value) return;
    if (peopleCount.value > 1) peopleCount.value -= 1;
  };

  const showQRCode = () => {
    qrModalVisible.value = true;
  };
  const hideQRCode = () => {
    qrModalVisible.value = false;
  };

  const submitOrder = async () => {
    // 订单一旦创建成功，提交入口只负责继续支付，不能再次创建订单。
    if (hasPendingPayment.value) {
      showPaymentConfirmModal();
      return;
    }

    if (isSubmittingOrder.value) return;
    if (orderItems.value.length === 0) return;

    if (orderItems.value.some((item) => !item.shopFoodSpecId || item.shopFoodSpecId <= 0)) {
      showToast('订单中存在未配置规格的菜品，请移除后重试');
      return;
    }

    if (isSpecialOrder.value && !selfPickDateTime.value) {
      showToast(`请选择${timeLabel.value}`);
      return;
    }

    // 检查营业时间
    const canProceed = await checkBusinessTime();
    if (!canProceed) {
      return;
    }

    if (cartType.value === 2) {
      showPaymentConfirmModal();
      return;
    }

    await createOrder();
  };

  const createOrder = async (): Promise<boolean> => {
    if (isSubmittingOrder.value || currentOrderId.value) return Boolean(currentOrderId.value);

    // 添加详细的调试信息
    console.log('=== 提交订单调试信息 ===');
    console.log('orderItems:', orderItems.value);
    console.log('subtotal:', subtotal.value);
    console.log('discountAmount:', discountAmount.value);
    console.log('total:', total.value);
    console.log('totalCount:', totalCount.value);
    console.log('peopleCount:', peopleCount.value);
    console.log('tableName:', tableName.value);
    console.log('shopId:', shopId.value);
    console.log('========================');

    // 构建符合接口要求的订单数据
    const orderData = {
      shopId: shopId.value,
      tableId: tableNumber.value, // 假设tableId就是tableNumber
      tableUniqueCode: `TABLE_${tableNumber.value}`, // 生成餐桌唯一标识
      customerId:
        selectedPaymentMethod.value === 100 ? foodTicketCustomer.value?.customerId : undefined,
      customerName:
        selectedPaymentMethod.value === 100 ? foodTicketCustomer.value?.customerName : undefined,
      userId: getEmployeeId(), // 使用保存的工号
      userName: getEmployeeName(), // 使用保存的工号，如果没有则使用默认值
      orderStatus: 1, // 订单状态：1-待确认
      orderNo: generateOrderNo(), // 生成订单编号
      orderType: 1, // 订单类型：1-堂食
      orderChannel: '服务员点餐', // 下单渠道
      dineType: dineType.value, // 就餐类型：1-堂食，2-自提，3-外卖，5-预定
      personCount: peopleCount.value, // 用餐人数
      totalAmount: total.value, // 总金额
      discountAmount: discountAmount.value, // 折扣金额
      foodAmount: subtotal.value, // 菜品金额
      serviceAmount: 0, // 服务费
      packageAmount: 0, // 包装费
      description: orderDescription.value.trim(),
      selfPickDate: isSpecialOrder.value ? selfPickDate.value : '',
      selfPickTime: isSpecialOrder.value ? selfPickTime.value : '',
      // description: `桌号：${tableName.value}，用餐人数：${peopleCount.value}人`,
      foodList: orderItems.value.map((item) => ({
        shopFoodId: item.id, // 使用菜品ID
        shopFoodSpecId: item.shopFoodSpecId, // 规格ID，默认1
        foodCookDesc: item.methodName || '', // 使用组合的methodName作为foodCookDesc
        quantity: item.quantity,
        price: item.price,
        amount: item.price * item.quantity,
        sourcePrice: item.price,
        sourceAmount: item.price * item.quantity,
        priority: 1, // 优先级
        addType: 1, // 添加类型：1-正常添加
        addPerson: getEmployeeName(), // 使用保存的工号
      })),
      paymentList: [], // 支付列表，提交订单时为空
    };

    console.log('完整订单数据:', orderData);

    isSubmittingOrder.value = true;
    try {
      // 调用提交订单接口
      const response = await fetchAddOrder(orderData);
      if (response.code === 200) {
        console.log('订单提交成功，订单ID:', response.result.orderId);

        // 保存订单信息；后续所有重试均复用此订单ID。
        currentOrderId.value = response.result.orderId;
        currentOrderNo.value = orderData.orderNo;
        tableOrderCompleteType.value = response.result.tableOrderCompleteType;
        if (cartType.value === 2) {
          return true;
        } else {
          alert(
            `${tableName.value}订单已提交成功！\n总金额：¥${total.value}\n共${totalCount.value}份菜品\n\n正在返回桌位管理页面...`,
          );
          router.push('/tableManage');
          return true;
        }
      } else {
        alert(response.msg || '订单提交失败，请稍后重试');
        return false;
      }
    } catch (error) {
      console.error('提交订单失败:', error);
      showToast('订单提交失败，请稍后重试');
      return false;
    } finally {
      isSubmittingOrder.value = false;
    }
  };

  // 生成订单编号
  const generateOrderNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    const second = String(now.getSeconds()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0');

    return `ORDER${year}${month}${day}${hour}${minute}${second}${random}`;
  };

  const goToHistory = () => {
    router.push('/orderHistory');
  };
  const showMenu = () => {
    // 可扩展：显示更多菜单
  };

  const adjustDiscount = () => {
    if (hasPendingPayment.value) {
      showToast('订单已创建，支付完成前不能修改订单');
      return;
    }
    tempDiscountMode.value = discountMode.value;
    tempManualDiscountAmount.value = manualDiscountAmount.value;
    tempDiscountRate.value = discountRate.value;
    discountModalVisible.value = true;
  };

  const hideDiscountModal = () => {
    discountModalVisible.value = false;
  };

  // 支付相关方法
  const showPaymentConfirmModal = () => {
    // 如果有B扫C收款选项，默认选择B扫C收款，否则选择POS支付
    selectedPaymentMethod.value = isOnlinePayment.value ? 3 : 4;
    paymentRemark.value = '';
    paymentConfirmModalVisible.value = true;
  };

  const handlePaymentMethodChange = () => {
    paymentRemark.value = '';
  };

  const resetFoodTicketEmployee = () => {
    foodTicketEmployeeNo.value = '';
    foodTicketCustomer.value = null;
  };

  const validateFoodTicketEmployee = async (): Promise<boolean> => {
    const employeeNo = foodTicketEmployeeNo.value.trim();
    if (!/^\d+$/.test(employeeNo)) {
      showToast('请输入正确的员工工号');
      return false;
    }

    const customerId = Number(employeeNo);
    if (!Number.isSafeInteger(customerId) || customerId <= 0) {
      showToast('请输入正确的员工工号');
      return false;
    }

    isValidatingEmployee.value = true;
    try {
      const response = await getCustomerList({
        pageNum: 1,
        pageSize: 10,
        data: { customerId, type: 1, status: 0 },
      });
      if (response.code !== 200) {
        showToast(response.msg || '员工信息查询失败');
        return false;
      }

      const matchedCustomers = (response.result?.list || []).filter(
        (customer) =>
          customer.customerId === customerId &&
          customer.type === 1 &&
          customer.status === 0 &&
          customer.disabled === 0,
      );
      if (matchedCustomers.length === 0) {
        showToast('未查询到可用的员工信息');
        return false;
      }
      if (matchedCustomers.length > 1) {
        showToast('员工工号重复，请联系管理员');
        return false;
      }

      foodTicketCustomer.value = matchedCustomers[0];
      foodTicketEmployeeNo.value = String(matchedCustomers[0].customerId);
      return true;
    } catch (error) {
      console.error('查询员工信息失败:', error);
      showToast('员工信息查询失败，请稍后重试');
      return false;
    } finally {
      isValidatingEmployee.value = false;
    }
  };

  const hidePaymentConfirmModal = () => {
    paymentConfirmModalVisible.value = false;
    // 如果有B扫C收款选项，默认选择B扫C收款，否则选择POS支付
    selectedPaymentMethod.value = isOnlinePayment.value ? 3 : 4;
    paymentRemark.value = '';
  };

  const cancelPaymentConfirmation = () => {
    hidePaymentConfirmModal();
    if (currentOrderId.value) {
      showToast('订单已创建，可点击“继续支付”重新发起支付');
    }
  };

  const handleSubmittedOrderFailure = async (message: string) => {
    hidePaymentConfirmModal();
    await showDialog({
      title: '订单已创建',
      message: `${message}\n\n订单已经创建，请前往桌位管理查看或继续处理，请勿重复下单。`,
    });
    router.replace('/tableManage');
  };

  // 处理支付确认（根据支付方式选择不同的处理逻辑）
  const handlePaymentConfirm = async () => {
    if (selectedPaymentMethod.value === 100) {
      if (unsupportedFoodTicketNames.value.length > 0) {
        const foodNames = unsupportedFoodTicketNames.value.join('、');
        if (!hasPendingPayment.value) {
          hidePaymentConfirmModal();
        }
        await showDialog({
          title: '菜品不支持餐券',
          message: hasPendingPayment.value
            ? `以下菜品不支持餐券使用：${foodNames}。订单已创建，请选择其他支付方式。`
            : `以下菜品不支持餐券使用：${foodNames}。请删除后再使用餐券支付。`,
        });
        return;
      }
      if (hasPendingPayment.value && !foodTicketCustomer.value) {
        showToast('该订单未绑定员工，不能改用餐券支付');
        return;
      }
      if (!foodTicketCustomer.value && !(await validateFoodTicketEmployee())) {
        return;
      }
      if (!hasEnoughFoodTicketBalance.value) {
        showToast('员工餐券余额不足，请选择其他支付方式');
        return;
      }
    }

    if (!currentOrderId.value && !(await createOrder())) {
      return;
    }

    if (selectedPaymentMethod.value === 3) {
      // B扫C收款，显示扫码界面
      hidePaymentConfirmModal();
      showScanPaymentModal();
    } else {
      // 其他支付方式，直接确认支付
      confirmPayment();
    }
  };

  // 处理支付完成后的逻辑（判断tableOrderCompleteType并调用相应接口）
  const handleAfterPayment = async () => {
    // 如果tableOrderCompleteType等于1，先调用全部出餐接口，然后调用核销接口
    if (tableOrderCompleteType.value === 1 && currentOrderId.value && shopId.value) {
      try {
        // 先调用全部出餐接口
        const foodUpResponse = await foodUp({
          orderId: currentOrderId.value,
          shopId: shopId.value,
        });
        if (foodUpResponse.code === 200) {
          // 全部出餐接口成功后，调用核销接口
          const completeResponse = await completeOrder({ orderId: currentOrderId.value });
          if (completeResponse.code === 200) {
            console.log('全部出餐并核销成功');
          } else {
            console.warn('核销失败:', completeResponse.msg || '接口返回错误');
          }
        } else {
          console.warn('全部出餐失败:', foodUpResponse.msg || '接口返回错误');
        }
      } catch (error) {
        console.error('处理支付后逻辑异常:', error);
      }
    }
  };

  // 确认支付（线下支付方式）
  const confirmPayment = async () => {
    try {
      if (!currentOrderId.value) {
        showDialog({ message: '订单信息不完整，无法确认支付' });
        return;
      }

      const employeeName = getEmployeeName();
      let remark = '';
      let paymentMethodName = '';

      // 根据选择的支付方式设置备注和支付方式名称
      if (selectedPaymentMethod.value === 3) {
        paymentMethodName = 'B扫C收款';
        remark = `服务员${employeeName}确认B扫C收款完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 4) {
        paymentMethodName = 'POS支付';
        remark = `服务员${employeeName}确认POS支付完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 5) {
        paymentMethodName = '公司挂账';
        remark = `服务员${employeeName}确认挂账，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 7) {
        paymentMethodName = '现金支付';
        remark = `服务员${employeeName}确认现金支付完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 100) {
        paymentMethodName = '餐券';
        remark = `服务员${employeeName}使用员工${foodTicketCustomer.value?.customerName}（工号${
          foodTicketCustomer.value?.customerId
        }）餐券支付，${paymentRemark.value || ''}`;
      }

      const paymentData: OrderPaymentModel = {
        orderId: currentOrderId.value,
        paymentMethod: selectedPaymentMethod.value,
        paymentMethodName: paymentMethodName,
        paymentStatus: 1, // 已支付
        amount: total.value,
        paymentTime: dateTimeFormat(dayjs()),
        remark: remark.trim(),
      };

      const response = await insertOrderPayment(paymentData);
      if (response.code === 200 && response.result.paymentStatus === 1) {
        hidePaymentConfirmModal();
        // 处理支付完成后的逻辑
        await handleAfterPayment();
        showDialog({
          message: `${tableName.value}${paymentMethodName}已确认完成！\n总金额：¥${total.value}\n共${totalCount.value}份菜品`,
        });
        // 延迟跳转，让用户看到成功提示
        setTimeout(() => {
          router.push('/tableManage');
        }, 1500);
      } else if (response.code === 200) {
        await showDialog({
          title: '支付失败',
          message:
            selectedPaymentMethod.value === 100
              ? '餐券扣款失败，请核对员工餐券余额后重试，或选择其他支付方式。'
              : '支付未成功，请核对后重试。',
        });
      } else {
        await handleSubmittedOrderFailure(response.msg || '支付或打印处理失败');
      }
    } catch (error) {
      console.error('确认支付失败:', error);
      await handleSubmittedOrderFailure('支付或打印处理失败，请稍后核对订单状态');
    }
  };

  // 显示扫码支付全屏界面
  const showScanPaymentModal = async () => {
    scanPaymentModalVisible.value = true;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
    // 立即设置为扫描中，避免显示"正在启动摄像头"提示
    isScanning.value = true;

    // 自动开始扫描
    await nextTick();
    getCamerasInfo();
  };

  // 隐藏扫码支付全屏界面
  const hideScanPaymentModal = () => {
    stopScan();
    scanPaymentModalVisible.value = false;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
  };

  // 用户主动退出扫码时返回支付方式选择，继续复用已创建的订单。
  const cancelScanPayment = () => {
    hideScanPaymentModal();
    showPaymentConfirmModal();
    showToast('订单已创建，请继续完成支付');
  };

  // 获取摄像头信息并初始化
  const getCamerasInfo = () => {
    // 立即设置为扫描中，隐藏"正在启动摄像头"提示
    isScanning.value = true;

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          html5QrCode = new Html5Qrcode('qr-reader');
          startScan();
        } else {
          // 没有可用摄像头
          isScanning.value = false;
          scanStatus.value = '未检测到摄像头设备';
          scanStatusClass.value = 'bg-red-100 text-red-700';
        }
      })
      .catch((err) => {
        console.error('获取摄像头失败:', err);
        scanStatus.value = '摄像头无访问权限！';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 开始扫描
  const startScan = () => {
    if (!html5QrCode) {
      console.error('html5QrCode 未初始化');
      return;
    }

    isScanning.value = true;
    scanStatus.value = '';
    scanStatusClass.value = '';

    html5QrCode
      .start(
        { facingMode: 'environment' },
        {
          fps: 20,
          // 全屏扫码：使用整个视窗大小
          qrbox: function (viewfinderWidth, viewfinderHeight) {
            return {
              width: viewfinderWidth,
              height: viewfinderHeight,
            };
          },
          // 不限制宽高比，允许全屏显示
          aspectRatio: undefined,
        },
        (decodedText, decodedResult) => {
          // 扫码成功回调
          handleScanResult(decodedText);
        },
      )
      .then(() => {
        // 扫描启动成功后，强制应用全屏样式
        nextTick(() => {
          forceFullscreenStyles();
        });
      })
      .catch((err) => {
        console.error('扫码失败', err);
        scanStatus.value = '启动摄像头失败，请检查权限设置';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 强制应用全屏样式
  const forceFullscreenStyles = () => {
    const qrReader = document.getElementById('qr-reader');
    if (!qrReader) return;

    // 强制设置容器样式
    const container = qrReader.querySelector('#html5qr-container');
    if (container) {
      (container as HTMLElement).style.width = '100%';
      (container as HTMLElement).style.height = '100%';
      (container as HTMLElement).style.minWidth = '100%';
      (container as HTMLElement).style.minHeight = '100%';
    }

    // 强制设置视频样式
    const videos = qrReader.querySelectorAll('video');
    videos.forEach((video) => {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.minWidth = '100%';
      video.style.minHeight = '100%';
      video.style.objectFit = 'cover';
      video.style.position = 'absolute';
      video.style.top = '0';
      video.style.left = '0';
      video.style.right = '0';
      video.style.bottom = '0';
    });

    // 强制设置画布样式
    const canvases = qrReader.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.minWidth = '100%';
      canvas.style.minHeight = '100%';
      canvas.style.objectFit = 'cover';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.right = '0';
      canvas.style.bottom = '0';
    });
  };

  // 停止扫描
  const stopScan = () => {
    if (html5QrCode && html5QrCode.isScanning) {
      html5QrCode
        .stop()
        .then(() => {
          html5QrCode?.clear();
          isScanning.value = false;
        })
        .catch((err) => {
          console.error('停止扫描失败:', err);
          isScanning.value = false;
        });
    } else {
      isScanning.value = false;
    }
  };

  // 处理扫描结果
  const handleScanResult = async (scanCode: string) => {
    // 验证扫码结果
    if (!scanCode || scanCode.trim() === '') {
      return;
    }

    // 停止扫描，但保持界面打开
    stopScan();

    // 显示处理中状态
    scanStatus.value = '扫码成功，正在处理支付...';
    scanStatusClass.value = 'bg-blue-100 text-blue-700';

    // 调用支付接口
    await submitScanPayment(scanCode);
  };

  // 提交扫码支付
  const submitScanPayment = async (scanCode: string) => {
    try {
      if (!currentOrderId.value) {
        scanStatus.value = '订单信息不完整，无法支付';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showDialog({ message: '订单信息不完整，无法支付' });
        return;
      }

      // 获取IP地址（简化处理，实际可以从服务器获取）
      const ip = await getClientIP();

      // 构建支付数据
      const paymentData = {
        orderId: currentOrderId.value,
        scanCode: scanCode,
        remark: scanPaymentRemark.value || '',
        ip: ip || '',
        amount: total.value,
      };

      // 调用扫码支付接口
      const response = await scanPayment(paymentData);

      // 根据接口返回结果处理
      if (response.code === 200) {
        // API 返回 200，立即关闭扫码界面
        hideScanPaymentModal();

        // 根据业务结果在详情界面显示提示
        if (response.result?.success) {
          // 支付成功
          // 处理支付完成后的逻辑
          await handleAfterPayment();
          showDialog({
            message: `扫码支付成功！\n总金额：¥${total.value}\n共${totalCount.value}份菜品`,
          });
          // 延迟跳转，让用户看到成功提示
          setTimeout(() => {
            router.push('/tableManage');
          }, 1500);
        } else {
          // 支付失败，显示确认模态框
          scanPaymentFailErrorMsg.value = response.result?.errorMsg || '';
          scanPaymentFailModalVisible.value = true;
        }
      } else {
        // API 返回非 200，支付结果可能不明确，交由服务员选择查询或重新扫码
        const errorMsg = response.msg || '支付失败，请重试';
        hideScanPaymentModal();
        scanPaymentFailErrorMsg.value = errorMsg;
        scanPaymentFailModalVisible.value = true;
      }
    } catch (error) {
      console.error('扫码支付失败:', error);

      // 网络异常时支付结果也可能不明确，交由服务员选择查询或重新扫码
      hideScanPaymentModal();
      scanPaymentFailErrorMsg.value = '支付失败，请稍后重试';
      scanPaymentFailModalVisible.value = true;
    }
  };

  // 获取客户端IP地址（简化版本）
  const getClientIP = async (): Promise<string> => {
    // 可以调用后端接口获取真实IP，这里先返回空字符串
    // 或者使用第三方服务获取IP
    return '';
  };

  // 确认已经支付（刷新扫码支付状态）
  const confirmScanPaymentPaid = async () => {
    // 防抖：如果正在刷新，直接返回
    if (isRefreshingPayment.value) {
      return;
    }

    try {
      if (!currentOrderId.value) {
        showDialog({ message: '订单信息不完整，无法确认支付' });
        return;
      }

      // 设置 loading 状态
      isRefreshingPayment.value = true;

      const response = await refreshScanPayment({
        orderId: currentOrderId.value,
      });

      if (response.code === 200) {
        // 根据业务结果处理
        if (response.result?.success) {
          // 支付成功，关闭模态框
          scanPaymentFailModalVisible.value = false;
          scanPaymentFailErrorMsg.value = '';
          // 处理支付完成后的逻辑
          await handleAfterPayment();
          showDialog({
            message: `支付成功！\n总金额：¥${total.value}\n共${totalCount.value}份菜品`,
          });
          // 延迟跳转，让用户看到成功提示
          setTimeout(() => {
            router.push('/tableManage');
          }, 1500);
        } else {
          // 支付失败，显示错误信息，模态框不关闭
          const errorMsg = response.result?.errorMsg || '刷新支付状态失败，请重试';
          scanPaymentFailErrorMsg.value = errorMsg;
          showDialog({ message: errorMsg });
        }
      } else {
        // API 返回非 200，显示错误信息，模态框不关闭
        const errorMsg = response.msg || '刷新支付状态失败，请稍后重试';
        scanPaymentFailErrorMsg.value = errorMsg;
        showDialog({ message: errorMsg });
      }
    } catch (error) {
      console.error('刷新支付状态失败:', error);
      const errorMsg = '刷新支付状态失败，请稍后重试';
      scanPaymentFailErrorMsg.value = errorMsg;
      showDialog({ message: errorMsg });
    } finally {
      // 无论成功失败，都要重置 loading 状态
      isRefreshingPayment.value = false;
    }
  };

  // 确认未支付后重新扫码，重新发起扫码支付
  const retryScanPayment = async () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    await showScanPaymentModal();
  };

  // 取消确认支付
  const cancelScanPaymentConfirm = () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    isRefreshingPayment.value = false; // 重置 loading 状态
    showPaymentConfirmModal();
  };

  const confirmDiscount = () => {
    if (
      tempDiscountMode.value === 'amount' &&
      (tempManualDiscountAmount.value < 0 || tempManualDiscountAmount.value > subtotal.value)
    ) {
      showToast('折扣金额必须在0和订单小计之间');
      return;
    }
    discountMode.value = tempDiscountMode.value;
    manualDiscountAmount.value = tempManualDiscountAmount.value;
    discountRate.value = tempDiscountRate.value;
    discountModalVisible.value = false;
  };

  const showSpecDialog = (item: any, event?: Event) => {
    if (hasPendingPayment.value) {
      showToast('订单已创建，支付完成前不能修改菜品');
      return;
    }
    if ((item.stockQuantity ?? 0) <= 0) {
      showToast('该菜品库存不足，暂时无法添加');
      return;
    }
    if (!item.specs || item.specs.length === 0) {
      showToast('该菜品未配置规格，暂时无法添加');
      return;
    }
    console.log('=== showSpecDialog 调试信息 ===');
    console.log('当前菜品:', item.name);
    console.log('cookList:', item.cookList);
    console.log('cookList长度:', item.cookList?.length);

    currentFood.value = item;
    // 设置默认规格（第一个规格）
    selectedSpec.value = item.specs[0];

    // 从item.cookList动态获取做法选项
    if (item.cookList && item.cookList.length > 0) {
      console.log('开始解析cookList...');
      methods.value = [];

      item.cookList.forEach((cook: any, cookIndex: number) => {
        console.log(`处理第${cookIndex + 1}个cook:`, cook);
        console.log('cookValue:', cook.cookValue);
        console.log('valueList:', cook.valueList);

        // 解析cookValue JSON字符串
        let cookValues = [];
        try {
          if (cook.cookValue) {
            cookValues = JSON.parse(cook.cookValue);
            console.log('解析cookValue成功:', cookValues);
          } else if (cook.valueList && cook.valueList.length > 0) {
            // 如果cookValue为空，使用valueList
            cookValues = cook.valueList;
            console.log('使用valueList:', cookValues);
          }
        } catch (error) {
          console.error('解析cookValue失败:', error);
          // 如果解析失败，使用valueList作为备用
          if (cook.valueList && cook.valueList.length > 0) {
            cookValues = cook.valueList;
            console.log('解析失败，使用valueList备用:', cookValues);
          }
        }

        // 将解析出的值添加到methods中，包含属性名称
        cookValues.forEach((valueItem: any, valueIndex: number) => {
          const value = valueItem.value || valueItem;
          const methodItem = {
            id: cook.cookId * 1000 + valueIndex, // 使用cookId * 1000 + valueIndex作为唯一ID
            name: value,
            cookId: cook.cookId,
            cookName: cook.cookName, // 属性名称，如"做法"、"配菜"
            cookType: cook.cookType,
            attributeName: cook.cookName, // 添加属性名称字段
          };
          methods.value.push(methodItem);
          console.log('添加做法选项:', methodItem);
        });
      });

      console.log('最终methods数组:', methods.value);
      // 设置默认选择第一个做法
      // 默认不选择任何做法
      selectedMethod.value = 0;

      // 初始化每个属性的默认选择
      selectedMethods.value = {};
      // 默认不选任何做法（包括常规），无需初始化条目
    } else {
      console.log('没有cookList，使用默认做法（不默认选中）');
      // 如果没有cookList，提供一个"常规"选项，但不默认选中
      methods.value = [{ id: 0, name: '常规' }];
      selectedMethod.value = 0;
      selectedMethods.value = {};
    }

    currentEvent.value = event; // 保存原始点击事件

    // 如果有做法选择，或者有多个规格，都需要弹窗
    if ((item.cookList && item.cookList.length > 0) || (item.specs && item.specs.length > 1)) {
      showSpecModalVisible.value = true;
    } else {
      // 没有做法且只有一个规格，直接添加到订单
      addToOrderWithSpec(item, event);
    }
  };

  const hideSpecModal = () => {
    showSpecModalVisible.value = false;
    currentFood.value = {};
    selectedSpec.value = {};
    selectedMethod.value = 0;
    selectedMethods.value = {}; // 清空多属性选择
    methods.value = []; // 清空做法列表
    currentEvent.value = undefined; // 清空原始点击事件
  };

  // 新增：切换做法选择（支持多选）
  const toggleMethodSelection = (attributeName: string, methodId: number) => {
    // 找到对应的cookType
    const method = methods.value.find((m) => m.id === methodId);
    const cookType = method?.cookType || 1; // 默认为多选

    if (cookType === 2) {
      // 单选：直接替换选择
      selectedMethods.value[attributeName] = methodId;
    } else {
      // 多选：支持多个选择
      if (!selectedMethods.value[attributeName]) {
        // 如果该属性还没有选择，初始化为数组
        selectedMethods.value[attributeName] = [methodId];
      } else if (Array.isArray(selectedMethods.value[attributeName])) {
        // 如果已经是数组，切换选择状态
        const currentSelection = selectedMethods.value[attributeName] as number[];
        const index = currentSelection.indexOf(methodId);
        if (index > -1) {
          // 如果已选中，则取消选择
          currentSelection.splice(index, 1);
          // 如果数组为空，删除该属性
          if (currentSelection.length === 0) {
            delete selectedMethods.value[attributeName];
          }
        } else {
          // 如果未选中，则添加选择
          currentSelection.push(methodId);
        }
      } else {
        // 如果之前是单选，转换为多选
        const currentId = selectedMethods.value[attributeName] as number;
        if (currentId === methodId) {
          // 如果点击的是已选中的，则取消选择
          delete selectedMethods.value[attributeName];
        } else {
          // 否则转换为多选
          selectedMethods.value[attributeName] = [currentId, methodId];
        }
      }
    }
  };

  // 新增：检查做法是否被选中
  const isMethodSelected = (attributeName: string, methodId: number): boolean => {
    const selection = selectedMethods.value[attributeName];
    // 找到对应的cookType
    const method = methods.value.find((m) => m.id === methodId);
    const cookType = method?.cookType || 1; // 默认为多选

    if (cookType === 2) {
      // 单选：直接比较
      return selection === methodId;
    } else {
      // 多选：检查是否在数组中
      if (Array.isArray(selection)) {
        return selection.includes(methodId);
      }
      return selection === methodId;
    }
  };

  const confirmAddToOrder = () => {
    if (hasPendingPayment.value) {
      hideSpecModal();
      showToast('订单已创建，支付完成前不能修改菜品');
      return;
    }
    console.log('=== 开始确认添加流程 ===');

    // 添加到订单
    addToOrderWithSpec(currentFood.value, currentEvent.value);

    // 立即开始动画，不等待DOM更新
    setTimeout(() => {
      flyFromSpecModal();
    }, 10);

    // 延长弹窗关闭时间，确保动画可见
    setTimeout(() => {
      hideSpecModal();
    }, 800);
  };

  const flyFromSpecModal = () => {
    console.log('=== 开始飞入动画 ===');
    console.log('当前菜品:', currentFood.value.name);

    // 直接查找当前显示的规格弹窗
    const specModal =
      document.querySelector('[data-modal="spec-modal"]') ||
      document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50:last-child');

    if (!specModal) {
      console.log('❌ 未找到规格弹窗');
      // 尝试备用方案
      const allModals = document.querySelectorAll('.fixed.inset-0.bg-black.bg-opacity-50');
      console.log('找到的所有弹窗:', allModals.length);
      if (allModals.length > 0) {
        const lastModal = allModals[allModals.length - 1];
        console.log('使用最后一个弹窗:', lastModal);
        startAnimation(lastModal);
      }
      return;
    }

    console.log('✅ 找到规格弹窗');
    startAnimation(specModal);
  };

  const startAnimation = (modal: Element) => {
    const img = modal.querySelector('img');
    if (!img) {
      console.log('❌ 未找到图片元素');
      return;
    }
    console.log('✅ 找到图片元素');

    if (!orderPanel.value) {
      console.log('❌ 未找到订单面板');
      return;
    }
    console.log('✅ 找到订单面板');

    const imgRect = img.getBoundingClientRect();
    const orderRect = orderPanel.value.getBoundingClientRect();

    console.log('图片位置:', {
      x: imgRect.left,
      y: imgRect.top,
      w: imgRect.width,
      h: imgRect.height,
    });
    console.log('订单面板位置:', {
      x: orderRect.left,
      y: orderRect.top,
      w: orderRect.width,
      h: orderRect.height,
    });

    // 确保图片有有效的位置信息
    if (imgRect.width === 0 || imgRect.height === 0) {
      console.log('❌ 图片尺寸无效');
      return;
    }

    // 创建克隆图片
    const clone = img.cloneNode(true) as HTMLImageElement;
    clone.style.position = 'fixed';
    clone.style.left = imgRect.left + 'px';
    clone.style.top = imgRect.top + 'px';
    clone.style.width = imgRect.width + 'px';
    clone.style.height = imgRect.height + 'px';
    clone.style.zIndex = '10000';
    clone.style.borderRadius = '8px';
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.5)';
    clone.style.transition = 'all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    clone.style.border = '3px solid #10b981';
    document.body.appendChild(clone);
    console.log('✅ 克隆图片已添加');

    // 计算目标位置
    const targetX = orderRect.left + orderRect.width / 2 - imgRect.width / 2;
    const targetY = orderRect.top + 50;

    console.log('目标位置:', { x: targetX, y: targetY });

    // 开始动画
    setTimeout(() => {
      clone.style.transform = `translate(${targetX - imgRect.left}px, ${
        targetY - imgRect.top
      }px) scale(0.2)`;
      clone.style.opacity = '0.3';
      console.log('🚀 动画已开始');
    }, 100);

    // 清理
    setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
        console.log('🧹 动画完成，已清理');
      }
    }, 1600);
  };

  const addToOrderWithSpec = (item: any, event?: Event) => {
    if (hasPendingPayment.value) return;
    if ((item.stockQuantity ?? 0) <= 0) {
      showToast('该菜品库存不足，暂时无法添加');
      return;
    }
    if (!item.specs || item.specs.length === 0) {
      showToast('该菜品未配置规格，暂时无法添加');
      return;
    }
    // 使用选中的规格信息
    const specInfo = selectedSpec.value;
    if (!specInfo.id || specInfo.id <= 0) {
      showToast('该菜品规格配置异常，暂时无法添加');
      return;
    }

    // 生成组合的methodName，包含所有选择的属性（支持多选）
    const methodNames: string[] = [];
    // 按照methods数组的显示顺序来构建methodName
    const attributeGroups: { [key: string]: string[] } = {};

    // 先收集所有选中的做法，按属性分组
    Object.keys(selectedMethods.value).forEach((attributeName) => {
      const selectedIds = selectedMethods.value[attributeName];
      if (Array.isArray(selectedIds)) {
        // 多选：收集选中的做法名称
        const selectedMethodNames = selectedIds
          .map((id) => {
            const method = methods.value.find((m) => m.id === id);
            return method ? method.name : '';
          })
          .filter((name) => name); // 过滤掉空值

        if (selectedMethodNames.length > 0) {
          attributeGroups[attributeName] = selectedMethodNames;
        }
      } else if (selectedIds) {
        // 单选：收集选中的做法名称
        const method = methods.value.find((m) => m.id === selectedIds);
        if (method) {
          attributeGroups[attributeName] = [method.name];
        }
      }
    });

    // 按照methods数组中的显示顺序来构建最终的methodName
    methods.value.forEach((method) => {
      const attributeName = method.attributeName || method.cookName || '做法';
      if (attributeGroups[attributeName] && attributeGroups[attributeName].includes(method.name)) {
        // 如果这个做法被选中了，添加到结果中
        if (!methodNames.includes(`${attributeName}:${method.name}`)) {
          methodNames.push(`${attributeName}:${method.name}`);
        }
      }
    });

    // 按照属性分组重新组织显示格式
    const groupedMethods: { [key: string]: string[] } = {};

    // 将选中的做法按属性分组
    methodNames.forEach((methodName) => {
      const [attributeName, methodValue] = methodName.split(':');
      if (!groupedMethods[attributeName]) {
        groupedMethods[attributeName] = [];
      }
      groupedMethods[attributeName].push(methodValue);
    });

    // 按照属性分组构建最终的显示文本
    const finalMethodNames: string[] = [];
    Object.keys(groupedMethods).forEach((attributeName) => {
      const methods = groupedMethods[attributeName];
      if (methods.length > 0) {
        finalMethodNames.push(`${attributeName}：${methods.join('，')}`);
      }
    });

    const combinedMethodName = finalMethodNames.join('，');

    const orderItem = {
      ...item,
      price: specInfo.price || item.price,
      shopFoodSpecId: specInfo.id,
      specName: specInfo.name || '标准',
      methodId: selectedMethod.value,
      methodName: combinedMethodName,
    };

    // 1. 订单逻辑
    const found = orderItems.value.find(
      (o) => o.id === item.id && o.shopFoodSpecId === orderItem.shopFoodSpecId,
    );
    if (found) {
      found.quantity += 1;
    } else {
      orderItems.value.push({
        ...orderItem,
        quantity: 1,
        shopFoodSpecId: orderItem.shopFoodSpecId,
      });
    }

    // 2. 动画效果 - 只有在直接点击菜品时才播放flyToOrder动画
    // 如果是从规格弹窗确认添加，则不播放这个动画（会由flyFromSpecModal处理）
    if (event && !showSpecModalVisible.value) {
      nextTick(() => {
        flyToOrder(event);
      });
    }

    console.log('添加到订单:', orderItem);
  };

  const loadShopDetailAndBusinessTime = async () => {
    try {
      const response = await fetchShopDetail({ shopId: shopId.value });
      if (response.code === 200) {
        shopDetail.value = response.result;
        businessTimeList.value = response.result.businessTimeList || [];
        // 判断支付方式：0线下、1线上
        isOnlinePayment.value = response.result.paymentLine === 1;
        // 如果有B扫C收款选项，默认选择B扫C收款
        if (isOnlinePayment.value) {
          selectedPaymentMethod.value = 3;
        }
      } else {
        showDialog({ message: response.msg || '加载店铺详情失败' });
      }
    } catch (error) {
      showDialog({ message: '加载店铺详情失败，请稍后重试' });
    }
  };

  const loadFoodTicketConfig = async () => {
    try {
      const response = await getShopConfigList({
        pageNum: 1,
        pageSize: 100,
        data: { configStatus: 0, configType: 1, shopId: shopId.value },
      });
      foodTicketEnabled.value =
        response.code === 200 &&
        (response.result?.list || []).some(
          (config) => config.configStatus === 0 && config.configKey === 100,
        );
    } catch (error) {
      console.error('加载餐券支付配置失败:', error);
      foodTicketEnabled.value = false;
    }
  };

  // 检查当前时间是否在营业时间内
  const checkBusinessTime = async (): Promise<boolean> => {
    // 如果没有营业时间配置，默认允许操作
    if (!businessTimeList.value || businessTimeList.value.length === 0) {
      return true;
    }

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes(),
    ).padStart(2, '0')}`;

    // 检查是否在任何一个营业时间段内
    const isInBusinessTime = businessTimeList.value.some((timeSlot) => {
      return currentTime >= timeSlot.beginTime && currentTime <= timeSlot.endTime;
    });

    if (isInBusinessTime) {
      return true;
    }

    // 不在营业时间内，显示确认对话框
    const timeSlots = businessTimeList.value
      .map((slot) => `${slot.beginTime}-${slot.endTime}`)
      .join('、');

    confirmMessage.value = `当前时间 ${currentTime} 不在营业时间内。\n\n营业时间：${timeSlots}\n\n确定要继续下单吗？`;
    confirmTitle.value = '营业时间提醒';
    const result = await confirmDialog.value?.show();
    return result || false;
  };

  const loadAvailableTimeList = async () => {
    if (!isSpecialOrder.value) return;

    try {
      let response;
      if (dineType.value === 5) {
        const shopFoodIds = [...new Set(orderItems.value.map((item) => item.id))];
        response = await getBookTimeList({ shopId: shopId.value, shopFoodIds });
      } else if (dineType.value === 3) {
        response = await getDeliveryTimeList({ shopId: shopId.value });
      } else {
        response = await getSelfPickTimeList({ shopId: shopId.value });
      }

      const list = response.code === 200 && Array.isArray(response.result) ? response.result : [];
      const timeMap: Record<string, string[]> = {};
      const dates: string[] = [];

      list.forEach((item) => {
        if (!item.selfPickDate) return;
        dates.push(item.selfPickDate);
        timeMap[item.selfPickDate] = (item.selfPickTimeList || [])
          .map((timeItem) => timeItem.selfPickTime)
          .filter((time): time is string => Boolean(time));
      });

      availableDates.value = dates;
      availableTimeMap.value = timeMap;
      selectedPickerDate.value = dates[0] || '';

      if (dates.length === 0) {
        showToast(`暂无可选${timeLabel.value}`);
      }
    } catch (error) {
      console.error(`加载${timeLabel.value}失败:`, error);
      availableDates.value = [];
      availableTimeMap.value = {};
      selectedPickerDate.value = '';
      showToast(`${timeLabel.value}加载失败，请稍后重试`);
    }
  };

  const showTimePicker = async () => {
    if (dineType.value === 5) {
      await loadAvailableTimeList();
    }
    if (availableDates.value.length === 0) {
      showToast(`暂无可选${timeLabel.value}`);
      return;
    }
    selectedPickerDate.value = selfPickDate.value || availableDates.value[0];
    const availableTimes = availableTimeMap.value[selectedPickerDate.value] || [];
    const selectedTime = availableTimes.includes(selfPickTime.value)
      ? selfPickTime.value
      : availableTimes[0] || '';
    timePickerValues.value = [selectedPickerDate.value, selectedTime];
    timePickerVisible.value = true;
  };

  const handleTimePickerChange = ({ selectedValues, columnIndex }: PickerChangeEventParams) => {
    if (columnIndex !== 0) return;

    const selectedDate = String(selectedValues[0] || '');
    if (selectedDate && selectedDate !== selectedPickerDate.value) {
      selectedPickerDate.value = selectedDate;
      timePickerValues.value = [selectedDate, availableTimeMap.value[selectedDate]?.[0] || ''];
    }
  };

  const handleTimePickerConfirm = ({ selectedValues }: PickerConfirmEventParams) => {
    const selectedDate = String(selectedValues[0] || '');
    const selectedTime = String(selectedValues[1] || '');
    if (!selectedDate || !selectedTime) {
      showToast(`请选择完整的${timeLabel.value}`);
      return;
    }
    selfPickDate.value = selectedDate;
    selfPickTime.value = selectedTime;
    timePickerVisible.value = false;
  };

  onMounted(() => {
    // 设置当前时间
    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${(now.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')} ${now
      .getHours()
      .toString()
      .padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    currentTime.value = formattedDate;
    tableNumber.value = Number(route.query.tableNumber) || 0;
    tableName.value = (route.query.tableName as string) || '';
    tableTag.value = typeof route.query.tableTag === 'string' ? route.query.tableTag : '';
    shopId.value = Number(route.query.shopId) || 0;
    shopName.value = (route.query.shopName as string) || '';
    cartType.value = Number(route.query.cartType) || 1;
    loadAllCategories();
    loadShopDetailAndBusinessTime();
    loadFoodTicketConfig();
    loadAvailableTimeList();
  });

  const loadAllCategories = async () => {
    try {
      const [categoryTagResp, foodResp] = await Promise.all([
        fetchCategoryTagList({
          // 新接口不分页，只按店铺查询分类+标签
          shopId: shopId.value,
        }),
        fetchCategoryItemList({
          pageNum: 1,
          pageSize: 999,
          data: { shopId: shopId.value, shopFoodStatus: 0 },
        }),
      ]);

      if (categoryTagResp.code === 200 && foodResp.code === 200) {
        // 新接口不分页，后端直接返回数组；为兼容性，这里同时兼容老的 result.list 结构
        const categoryTagResult: any = categoryTagResp.result;
        const categoryTagList: any[] = Array.isArray(categoryTagResult)
          ? categoryTagResult
          : categoryTagResult?.list || [];
        const foodList: any[] = foodResp.result?.list || [];

        console.log('=== 分类+标签及菜品调试 ===');
        console.log('分类/标签列表:', categoryTagList);
        console.log('菜品列表原始数据:', foodList);
        if (foodList.length > 0) {
          console.log('第一个菜品数据:', foodList[0]);
          console.log('第一个菜品的cookList:', foodList[0]?.cookList);
        }

        // 先构建所有菜品的完整列表
        const allItems: Array<CategoryItemModel> = [];
        foodList.forEach((item: any) => {
          const specItems: Array<{
            id: number;
            name: string;
            price: number;
            specId: number;
            specName: string;
          }> = [];
          (item.specList || []).forEach((specItem: any) => {
            specItems.push({
              id: specItem.shopFoodSpecId,
              name: specItem.specName,
              price: specItem.price,
              specId: specItem.specId,
              specName: specItem.specName,
            });
          });
          allItems.push({
            id: item.shopFoodId,
            name: item.shopFoodName,
            foodId: item.foodId,
            foodName: item.foodName,
            shopFoodStatus: item.shopFoodStatus,
            isFoodTicket: item.isFoodTicket,
            price: item.price,
            description: item.shopFoodDesc,
            foodType: item.foodType,
            image: item.imageUrl,
            stockQuantity: item.stockQuantity ?? 0,
            specs: specItems,
            shopFoodSpecId: specItems.length > 0 ? specItems[0].id : undefined,
            cookList: item.cookList || [],
          });
        });
        const menuFoodIds = new Set(
          foodList
            .filter((item: any) => {
              const tags = String(item.tags || '')
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean);
              return tags.includes(menuTag.value);
            })
            .map((item: any) => item.shopFoodId),
        );
        const shouldFilterByMenuTag = menuFoodIds.size > 0;

        allMenuItems.value = shouldFilterByMenuTag
          ? allItems.filter((item) => item.id !== undefined && menuFoodIds.has(item.id))
          : allItems;

        // 桌号类型直接与菜品 tags 匹配，不依赖分类或推荐标签配置。
        const visibleCategoryTagList = shouldFilterByMenuTag
          ? [{ id: 0, name: menuTag.value, type: 1 }]
          : categoryTagList;

        categories.value = [
          {
            id: 0,
            name: '全部',
            items: allMenuItems.value,
          },
        ];

        // 根据返回的 type 构建分类：
        // type = 1 -> 标签：按菜品的 tags 字段模糊匹配
        // type = 2 -> 分类：按原来的分类匹配逻辑（shopFoodCategoryId === id）
        visibleCategoryTagList.forEach((ct: any) => {
          const ctId = ct.id;
          const ctName = ct.name;
          const ctType = ct.type; // 1=标签，2=分类

          let itemList: any[] = [];
          if (ctType === 2) {
            // 分类：按分类ID匹配
            itemList = foodList.filter((item: any) => item.shopFoodCategoryId === ctId);
          } else if (ctType === 1) {
            // 标签：按 tags 字段模糊匹配
            itemList = foodList.filter((item: any) => {
              const tagsStr = String(item.tags || '');
              return tagsStr
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .includes(String(ctName || '').trim());
            });
          } else {
            // 其他类型暂不处理
            return;
          }

          // 当前分类/标签下的菜品列表（在 allItems 中按 shopFoodId 过滤）
          const items: Array<CategoryItemModel> = allItems.filter((it) =>
            itemList.some((origin) => origin.shopFoodId === it.id),
          );

          categories.value.push({
            id: ctId,
            name: ctName,
            categoryType: ctType,
            items: items,
          });
        });

        console.log(categories.value);
        selectedCategory.value = shouldFilterByMenuTag
          ? menuTag.value
          : categories.value[0]?.name || '';
      } else {
        showDialog({
          message: categoryTagResp.msg || foodResp.msg || '加载菜品分类失败，请稍后重试',
        });
      }
    } catch (error) {
      console.error('加载菜品分类失败：', error);
      showDialog({ message: '加载菜品分类失败，请稍后重试' });
    }
  };
</script>

<style scoped>
  .payment-modal-overlay {
    padding: 16px;
  }

  .payment-modal-card {
    display: flex;
    flex-direction: column;
    width: min(720px, calc(100vw - 32px));
    max-height: calc(100vh - 32px);
    overflow: hidden;
    padding: 24px;
  }

  .payment-modal-header {
    flex-shrink: 0;
    margin-bottom: 16px;
  }

  .payment-modal-body {
    display: grid;
    min-height: 0;
    overflow-y: auto;
    gap: 16px;
    padding-right: 2px;
  }

  .payment-order-summary,
  .payment-form {
    min-width: 0;
  }

  .employee-validation-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }

  .employee-validation-button {
    min-width: 76px;
    white-space: nowrap;
  }

  .payment-modal-footer {
    flex-shrink: 0;
    margin-top: 16px;
    padding-top: 16px;
  }

  @media (min-width: 768px) {
    .payment-modal-body {
      grid-template-columns: minmax(220px, 0.8fr) minmax(320px, 1.2fr);
      align-items: start;
    }
  }

  @media (max-height: 700px) {
    .payment-modal-overlay {
      padding: 8px;
    }

    .payment-modal-card {
      max-height: calc(100vh - 16px);
      padding: 16px;
    }

    .payment-modal-header,
    .payment-modal-footer {
      margin-bottom: 10px;
    }

    .payment-modal-footer {
      margin-top: 10px;
      padding-top: 10px;
    }
  }

  .category-item {
    padding: 16px 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.2s;
  }

  .category-item.active {
    background-color: #f0f9ff;
    color: #3b82f6;
    font-weight: 500;
    border-left: 3px solid #3b82f6;
  }

  .category-item:hover {
    background-color: #f8f8f8;
  }

  .menu-item {
    display: flex;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
  }

  .menu-item:hover {
    background-color: #f9fafb;
  }

  .menu-item.menu-item-disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .menu-item.menu-item-disabled:hover {
    background-color: transparent;
  }

  .menu-item-image {
    width: 70px;
    height: 70px;
    border-radius: 8px;
    overflow: hidden;
    margin-right: 12px;
    flex-shrink: 0;
    position: relative;
  }

  .menu-item-content {
    flex: 1;
    text-align: left;
  }

  .menu-item-title {
    font-weight: 600;
    font-size: 22px;
    margin-bottom: 4px;
  }

  .menu-item-desc {
    font-size: 18px;
    color: #6b7280;
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .menu-item-price {
    color: #ef4444;
    font-weight: 500;
    font-size: 20px;
  }

  .menu-item-stock {
    margin-left: 12px;
    color: #6b7280;
    font-size: 16px;
  }

  .menu-item-stock.out-of-stock {
    color: #ef4444;
    font-weight: 500;
  }

  .menu-item-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .menu-item-btn.increase {
    background-color: #3b82f6;
    color: white;
  }

  .menu-item-btn.decrease {
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .menu-item-count {
    min-width: 40px;
    text-align: center;
    font-size: 20px;
  }

  .order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 7px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .order-item-info {
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  .order-item-name {
    font-weight: 500;
    font-size: 18px;
    line-height: 1.3;
    margin-bottom: 1px;
  }

  .order-item-price {
    font-size: 16px;
    line-height: 1.3;
    color: #ef4444;
  }

  .order-item-spec {
    font-size: 14px;
    line-height: 1.25;
    margin-bottom: 1px;
  }

  .order-item-controls {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .order-item .menu-item-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }

  .order-item .menu-item-count {
    min-width: 28px;
    font-size: 17px;
  }

  /* html5-qrcode 全屏样式 - 强制全屏显示，消除黑边 */
  .qr-reader-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* html5-qrcode 的所有内部容器都强制全屏 */
  .qr-reader-container > *,
  .qr-reader-container #html5qr-container,
  .qr-reader-container #html5qr-container > * {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
  }

  /* 视频元素 - 强制覆盖整个容器，消除黑边 */
  .qr-reader-container video,
  .qr-reader-container #html5qr-container video {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important; /* 覆盖整个容器，裁剪多余部分 */
    object-position: center !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* 画布元素 - 用于二维码识别 */
  .qr-reader-container canvas,
  .qr-reader-container #html5qr-container canvas {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  /* html5-qrcode 的内部容器 */
  .qr-reader-container #html5qr-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* 隐藏扫描类型切换按钮 */
  .qr-reader-container #html5-qrcode-anchor-scan-type-change {
    display: none !important;
  }

  /* 隐藏扫描框遮罩区域 */
  .qr-reader-container #qr-shaded-region {
    display: none !important;
  }

  /* 隐藏扫描框边框 */
  .qr-reader-container #qr-shaded-region,
  .qr-reader-container #qr-shaded-region > * {
    display: none !important;
  }

  /* 动态扫描线 - 类似微信扫码效果 */
  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 255, 0, 0.8) 50%,
      transparent 100%
    );
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.6), 0 0 20px rgba(0, 255, 0, 0.4);
    animation: scanMove 2s linear infinite;
  }

  @keyframes scanMove {
    0% {
      top: 0;
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    100% {
      top: 100%;
      opacity: 0.3;
    }
  }
</style>
